import React, { useState } from 'react';
import './VoiceSearch.css';

interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  vitamins?: Array<{
    name: string;
    amount: number;
    unit: string;
  }>;
  minerals?: Array<{
    name: string;
    amount: number;
    unit: string;
  }>;
}

interface VoiceSearchResult {
  query: string;
  results_count: number;
  products: Product[];
}

const VoiceSearch: React.FC = () => {
  const [isListening, setIsListening] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<VoiceSearchResult | null>(null);
  const [llmAnswer, setLlmAnswer] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Browser Speech Recognition
  const startVoiceSearch = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Spracherkennung wird in deinem Browser nicht unterstützt');
      return;
    }

    const recognition = new (window as any).webkitSpeechRecognition();
    recognition.lang = 'de-DE';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      performSearch(transcript);
    };

    recognition.onerror = (event: any) => {
      console.error('Spracherkennungsfehler:', event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  // Textsuche
  const performSearch = async (query: string) => {
    setLoading(true);
    try {
      const response = await fetch('/api/voice-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: query, language: 'de' }),
      });

      const data = await response.json();
      setResults(data);
    } catch (error) {
      console.error('Suchfehler:', error);
    } finally {
      setLoading(false);
    }
  };

  // Kombinierte Suche + LLM
  const searchAndAsk = async (question: string) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/search-and-ask?question=${encodeURIComponent(question)}`, {
        method: 'POST',
      });

      const data = await response.json();
      setResults({
        query: data.question,
        results_count: data.found_products,
        products: data.products,
      });
      setLlmAnswer(data.llm_answer || '');
    } catch (error) {
      console.error('Fehler bei kombinierter Suche:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="voice-search-container">
      <div className="voice-search-header">
        <h1>🎤 Sprachsuche für Getränke & Nahrungsergänzung</h1>
      </div>
      
      {/* Suchleiste */}
      <div className="search-bar-container">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && performSearch(searchQuery)}
          placeholder="Suche nach Getränken, Vitaminen, Produkten..."
          className="search-input"
        />
        
        <button
          onClick={startVoiceSearch}
          disabled={isListening}
          className={`btn btn-voice ${isListening ? 'listening' : ''}`}
        >
          {isListening ? '🎤 Höre zu...' : '🎤 Sprache'}
        </button>

        <button
          onClick={() => performSearch(searchQuery)}
          disabled={!searchQuery || loading}
          className="btn btn-search"
        >
          🔍 Suchen
        </button>

        <button
          onClick={() => searchAndAsk(searchQuery)}
          disabled={!searchQuery || loading}
          className="btn btn-ai"
        >
          🤖 KI fragen
        </button>
      </div>

      {/* LLM Antwort */}
      {llmAnswer && (
        <div className="llm-answer-box">
          <h3>🤖 KI-Antwort:</h3>
          <p>{llmAnswer}</p>
        </div>
      )}

      {/* Lade-Anzeige */}
      {loading && (
        <div className="loading-indicator">
          <div className="loading-spinner"></div>
          <p>Suche läuft...</p>
        </div>
      )}

      {/* Suchergebnisse */}
      {results && !loading && (
        <div className="results-section">
          <div className="results-header">
            <h2>
              Suchergebnisse für "<span className="results-count">{results.query}</span>" ({results.results_count} gefunden)
            </h2>
          </div>

          <div className="product-grid">
            {results.products.map((product) => (
              <div key={product.id} className="product-card">
                <h3>{product.name}</h3>
                <p className="product-description">
                  {product.description || 'Keine Beschreibung verfügbar'}
                </p>
                
                <p className="product-price">
                  {product.price.toFixed(2)} €
                </p>

                {/* Vitamine */}
                {product.vitamins && product.vitamins.length > 0 && (
                  <div className="nutritional-info">
                    <h4>💊 Vitamine:</h4>
                    <ul>
                      {product.vitamins.map((vitamin, idx) => (
                        <li key={idx}>
                          {vitamin.name}: {vitamin.amount}{vitamin.unit}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Mineralstoffe */}
                {product.minerals && product.minerals.length > 0 && (
                  <div className="nutritional-info">
                    <h4>⚡ Mineralstoffe:</h4>
                    <ul>
                      {product.minerals.map((mineral, idx) => (
                        <li key={idx}>
                          {mineral.name}: {mineral.amount}{mineral.unit}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button className="add-to-cart-btn">
                  In den Warenkorb
                </button>
              </div>
            ))}
          </div>

          {results.results_count === 0 && (
            <div className="empty-state">
              <p>Keine Produkte gefunden. Versuche es mit einem anderen Suchbegriff.</p>
            </div>
          )}
        </div>
      )}

      {/* Beispielsuchen */}
      {!results && !loading && (
        <div className="example-searches">
          <h3>💡 Beispielsuchen:</h3>
          <div className="example-tags">
            {[
              'Bier alkoholfrei',
              'Coca Cola',
              'Mineralwasser',
              'Vitamin C',
              'Magnesium',
              'Rotwein',
              'Energy Drink',
              'Apfelschorle',
            ].map((example) => (
              <button
                key={example}
                onClick={() => {
                  setSearchQuery(example);
                  performSearch(example);
                }}
                className="example-tag"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceSearch;
